import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShieldCheck, Globe2, Building2, MapPin, Users, Cpu, FileSearch, ArrowRight } from 'lucide-react';

const DILIGENCE = [
  { icon: Building2, title: 'Purchaser', text: 'Company legal name, country of incorporation and headquarters location.' },
  { icon: Globe2, title: 'Ultimate parent', text: 'Parent company and its headquarters country — some requirements follow the ultimate parent even when the immediate buyer is elsewhere.' },
  { icon: MapPin, title: 'Destination', text: 'Delivery destination and the country of final installation or deployment.' },
  { icon: Users, title: 'End user & end use', text: 'Whether the buyer is the ultimate end user, the end user’s identity and country, and the intended application.' },
  { icon: Cpu, title: 'Product classification', text: 'The specific product or system and its export classification (ECCN) where established — never assumed.' },
  { icon: FileSearch, title: 'Re-export & licensing', text: 'Any intended re-export or resale, and whether a licence or licence exception applies to the route.' },
];

const OUTCOMES = [
  { code: 'REVIEW_REQUIRED', label: 'Review required', text: 'Automatically applied to every enquiry involving export-controlled hardware. Nothing is approved or refused at this stage.' },
  { code: 'IN_REVIEW', label: 'In review', text: 'An authorised Real Tech administrator has opened a case file and is assessing the transaction.' },
  { code: 'CLEARED', label: 'Cleared', text: 'The transaction may proceed as described.' },
  { code: 'CLEARED_WITH_CONDITIONS', label: 'Cleared with conditions', text: 'The transaction may proceed subject to stated conditions, such as end-user undertakings or documentation.' },
  { code: 'LICENCE_REQUIRED', label: 'Licence required', text: 'Supply depends on an export licence or authorisation being obtained.' },
  { code: 'ON_HOLD', label: 'On hold', text: 'Further information or documentation has been requested before a determination can be made.' },
  { code: 'DECLINED', label: 'Declined', text: 'The transaction cannot be supplied as described under applicable export-control or sanctions law.' },
];

const fade = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };

export default function CompliancePage() {
  return (
    <main className="pt-32 pb-20" data-testid="compliance-page">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <motion.div {...fade} className="mb-14">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0096D6]/10 text-[#0096D6] font-manrope text-sm font-medium mb-4">
            <ShieldCheck className="w-4 h-4" /> Export Compliance
          </span>
          <h1 className="font-outfit text-4xl md:text-5xl font-bold text-slate-900 mb-5" data-testid="compliance-heading">
            Purchasing Requirements for Advanced Computing Hardware
          </h1>
          <p className="font-manrope text-slate-600 text-lg leading-relaxed max-w-3xl">
            GPUs, AI accelerators, multi-GPU systems and certain other high-performance computing products can be subject to export-control, sanctions and licensing requirements. Whether a particular transaction may proceed depends on the specific product, its classification, the destination, the parties involved, the end use and the available licence or exception — not on any single factor.
          </p>
        </motion.div>

        <motion.section {...fade} className="mb-14" data-testid="compliance-why">
          <h2 className="font-outfit text-base md:text-lg font-semibold text-slate-900 uppercase tracking-wide mb-6">What we ask, and why</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {DILIGENCE.map((item) => (
              <div key={item.title} className="rounded-2xl bg-white border border-slate-200/80 p-6 flex gap-4">
                <span className="w-10 h-10 rounded-xl bg-[#0096D6]/10 flex items-center justify-center shrink-0">
                  <item.icon className="w-5 h-5 text-[#0096D6]" />
                </span>
                <div>
                  <h3 className="font-outfit font-semibold text-slate-900 mb-1">{item.title}</h3>
                  <p className="font-manrope text-sm text-slate-600 leading-relaxed">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="font-manrope text-sm text-slate-500 mt-6 leading-relaxed">
            This mirrors the due-diligence approach recommended by export-control authorities: identifying the purchaser, intermediate and ultimate consignees, end user, headquarters and ultimate-parent locations, intended use, installation location, product classification and any potential re-export.
          </p>
        </motion.section>

        <motion.section {...fade} className="mb-14" data-testid="compliance-outcomes">
          <h2 className="font-outfit text-base md:text-lg font-semibold text-slate-900 uppercase tracking-wide mb-3">How the review works</h2>
          <p className="font-manrope text-slate-600 mb-6 leading-relaxed max-w-3xl">
            Every enquiry involving export-controlled hardware is classified <span className="font-semibold text-slate-900">Review Required</span>. No transaction is approved or refused automatically, and we do not operate a simple country list: an authorised Real Tech Services administrator assesses each case on the product, destination, parties, end use and licensing position.
          </p>
          <div className="rounded-2xl bg-white border border-slate-200/80 divide-y divide-slate-100">
            {OUTCOMES.map((o) => (
              <div key={o.code} className="p-5 flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-6" data-testid={`compliance-outcome-${o.code.toLowerCase()}`}>
                <span className="sm:w-56 shrink-0 font-space text-xs font-semibold tracking-wider text-[#0096D6] uppercase pt-0.5">{o.label}</span>
                <p className="font-manrope text-sm text-slate-600 leading-relaxed">{o.text}</p>
              </div>
            ))}
          </div>
        </motion.section>

        <motion.section {...fade} className="mb-14 rounded-2xl bg-slate-900 text-white p-8 md:p-10" data-testid="compliance-commitments">
          <h2 className="font-outfit text-base md:text-lg font-semibold uppercase tracking-wide mb-5">Our commitments</h2>
          <ul className="space-y-3 font-manrope text-sm text-white/80 leading-relaxed">
            <li>• Products will not be supplied where a transaction would breach applicable export-control or sanctions laws.</li>
            <li>• Quoting availability or price does not constitute confirmation of export eligibility.</li>
            <li>• We may request additional end-user, destination and corporate documentation before accepting an order.</li>
            <li>• Export classifications are established from supplier and manufacturer data, never inferred.</li>
            <li>• Market communications and compliance review are separate: receiving an offer does not imply a transaction has been cleared.</li>
          </ul>
        </motion.section>

        <motion.div {...fade} className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between rounded-2xl border border-slate-200 p-6 md:p-8 bg-white">
          <div>
            <p className="font-outfit text-xl font-semibold text-slate-900 mb-1">Enquire about advanced compute hardware</p>
            <p className="font-manrope text-sm text-slate-600">Our enquiry form captures the information above so review can begin immediately.</p>
          </div>
          <Link to="/contact?enquiry=advanced-compute#enquiry-form" className="btn-primary inline-flex items-center gap-2 whitespace-nowrap" data-testid="compliance-enquire-btn">
            Start an Enquiry <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>

        <p className="font-manrope text-xs text-slate-400 mt-8">This page is provided for general information and does not constitute legal advice. Applicable requirements vary by product, jurisdiction and transaction.</p>
      </div>
    </main>
  );
}
